import React from 'react';
import { ChevronRight, MessageSquareText, Package, Scale, Smartphone } from 'lucide-react';
import { TaskyTextarea, TaskyInput } from '../tasky';
import {
  CastAge,
  CastGender,
  UgcFormatType,
  UgcTargetSeconds,
} from '../../services/api/ugcAdService';

/**
 * UGC動画広告の作成設定（構成タイプ・出演者・尺・自由コメント・詳細設定）の入力フォーム本体。
 * song-ad の SongAdSettingsForm と同じ役割分担で、新規作成後の①情報登録タブ（InfoStep）から使う
 * （商品選択・タイトル・送信ボタンは呼び出し側が持つ）。
 *
 * アスペクト比は 9:16 固定なのでセレクタを出さず、固定表示だけ置く
 * （TikTok/Reels/Shorts 向け。16:9 等は当面提供しない）。
 */

export interface UgcAdSettingsValue {
  formatType: UgcFormatType;
  castGender: CastGender;
  castAge: CastAge;
  targetSeconds: UgcTargetSeconds;
  freeText: string;
  requiredWordsText: string;
  bannedWordsText: string;
}

export const defaultUgcAdSettings: UgcAdSettingsValue = {
  formatType: 'balance',
  castGender: 'auto',
  castAge: 'auto',
  targetSeconds: 30,
  freeText: '',
  requiredWordsText: '',
  bannedWordsText: '',
};

export const isValidUgcTargetSeconds = (sec: number): sec is UgcTargetSeconds =>
  sec === 15 || sec === 30 || sec === 45 || sec === 60;

/** 「必ず含める言葉」「避ける言葉」入力（、/,/改行区切り）を配列へ変換する */
export const splitWordsInput = (text: string): string[] =>
  text
    .split(/[,、\n]/)
    .map((w) => w.trim())
    .filter(Boolean);

export const joinWordsInput = (words: string[]): string => words.join('、');

interface FormatTypeOption {
  key: UgcFormatType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  rec: UgcTargetSeconds[];
  desc: string;
}

/**
 * ショート動画の構成タイプ3種。
 * 他社比較（Arcads=トーキングアクター特化 / TopView・UGCast=商品合成・実演系 / 各社のミックス構成）
 * を踏まえ、「人が話す量」を軸にした3択にする。技術の選択ではなく仕上がりの選択として書く。
 */
export const FORMAT_TYPES: FormatTypeOption[] = [
  {
    key: 'talk',
    label: 'トーク中心',
    icon: MessageSquareText,
    rec: [15, 30],
    desc: '出演者がカメラに向かって体験談やおすすめ理由を話します。レビュー・共感訴求向け。',
  },
  {
    key: 'balance',
    label: 'バランス型',
    icon: Scale,
    rec: [30, 45],
    desc: 'トークと商品カットを交互に組み合わせます。迷ったらこれ。',
  },
  {
    key: 'product',
    label: '商品フォーカス',
    icon: Package,
    rec: [15, 30],
    desc: '商品のアップ・使用シーンが中心。伝えたいことはテロップとナレーションで補います。',
  },
];

export const CAST_GENDER_OPTIONS: { key: CastGender; label: string }[] = [
  { key: 'auto', label: 'おまかせ' },
  { key: 'female', label: '女性' },
  { key: 'male', label: '男性' },
];

export const CAST_AGE_OPTIONS: { key: CastAge; label: string }[] = [
  { key: 'auto', label: 'おまかせ' },
  { key: '10s', label: '10代' },
  { key: '20s', label: '20代' },
  { key: '30s', label: '30代' },
  { key: '40s', label: '40代' },
  { key: '50s', label: '50代' },
  { key: '60s', label: '60代以上' },
];

const DURATIONS: { sec: UgcTargetSeconds; hint: string }[] = [
  { sec: 15, hint: 'フックと一言で押し切る' },
  { sec: 30, hint: 'フック→訴求→CTAの標準形' },
  { sec: 45, hint: '特徴やビフォーアフターを足す' },
  { sec: 60, hint: '使い方や体験談をじっくり' },
];

const selCardClass = (selected: boolean): string =>
  `rounded-xl border text-left transition-colors ${
    selected
      ? 'border-primary bg-primary/5'
      : 'border-warm-border bg-white hover:border-primary/40'
  }`;

const segButtonClass = (selected: boolean): string =>
  `flex-1 px-3 py-2 text-sm transition-colors ${
    selected ? 'bg-primary text-white font-medium' : 'text-gray-600 hover:bg-gray-100'
  }`;

export interface UgcAdSettingsFormProps {
  value: UgcAdSettingsValue;
  onChange: (patch: Partial<UgcAdSettingsValue>) => void;
  disabled?: boolean;
  advancedDefaultOpen?: boolean;
  productNamePlaceholder?: string;
  /**
   * 出演者（性別・年代）の直後に差し込む「出演者の顔（リファレンス画像）」ピッカー。
   * 候補生成などプロジェクトAPIに依存するため中身は InfoStep 側が持ち、
   * このフォームは配置だけを提供する（作成パネルからは渡さない＝表示されない）。
   */
  castReferenceSlot?: React.ReactNode;
}

const UgcAdSettingsForm: React.FC<UgcAdSettingsFormProps> = ({
  value,
  onChange,
  disabled = false,
  advancedDefaultOpen = false,
  productNamePlaceholder,
  castReferenceSlot,
}) => {
  const [advOpen, setAdvOpen] = React.useState(advancedDefaultOpen);
  const recDurations = FORMAT_TYPES.find((f) => f.key === value.formatType)?.rec ?? [];

  return (
    <div className="space-y-5">
      {/* 構成タイプ */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">構成タイプ</p>
        <div className="grid md:grid-cols-3 gap-3">
          {FORMAT_TYPES.map((f) => {
            const selected = value.formatType === f.key;
            return (
              <button
                key={f.key}
                type="button"
                disabled={disabled}
                onClick={() => onChange({ formatType: f.key })}
                className={`${selCardClass(selected)} p-3.5`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <f.icon className={`w-[19px] h-[19px] ${selected ? 'text-primary' : 'text-gray-400'}`} />
                  <span className="text-sm font-semibold text-dark">{f.label}</span>
                  <span className="ml-auto text-[10px] text-gray-400 bg-warm-surface rounded-full px-2 py-0.5">
                    推奨 {f.rec.join('・')}秒
                  </span>
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">{f.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* 出演者（性別 / 年代） */}
      <div className="grid md:grid-cols-2 gap-x-8 gap-y-5">
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">出演者の性別</p>
          <div className="inline-flex w-full rounded-lg overflow-hidden border border-warm-border">
            {CAST_GENDER_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                disabled={disabled}
                onClick={() => onChange({ castGender: opt.key })}
                className={segButtonClass(value.castGender === opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="flex items-baseline gap-2 mb-2">
            <p className="text-sm font-medium text-gray-700">出演者の年代</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {CAST_AGE_OPTIONS.map((opt) => {
              const selected = value.castAge === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  disabled={disabled}
                  onClick={() => onChange({ castAge: opt.key })}
                  className={`rounded-full pl-3 pr-3.5 py-1.5 text-[13px] border transition-colors ${
                    selected
                      ? 'bg-primary text-white border-primary'
                      : 'border-warm-border text-gray-600 hover:border-primary'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* 出演者の顔（リファレンス画像）。InfoStep から渡されたときだけ表示 */}
      {castReferenceSlot}

      {/* 動画の長さ */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">動画の長さ</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {DURATIONS.map((d) => {
            const selected = value.targetSeconds === d.sec;
            const recommended = recDurations.includes(d.sec);
            return (
              <button
                key={d.sec}
                type="button"
                disabled={disabled}
                onClick={() => onChange({ targetSeconds: d.sec })}
                className={`${selCardClass(selected)} p-3`}
              >
                <div className="flex items-baseline gap-1">
                  <span className="text-lg font-bold text-dark">{d.sec}</span>
                  <span className="text-xs text-gray-500">秒</span>
                  {recommended && (
                    <span className="ml-auto text-[10px] text-primary bg-primary/10 rounded-full px-2 py-0.5">
                      推奨
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">{d.hint}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* アスペクト比（9:16固定） */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">アスペクト比</p>
        <div className="inline-flex items-center gap-3 rounded-xl border border-warm-border bg-warm-surface px-4 py-2.5">
          <span
            className="block rounded-[3px] border-2 border-primary bg-primary/10"
            style={{ width: 16.9, height: 30 }}
          />
          <span className="text-left">
            <span className="block text-sm font-semibold text-dark">
              9:16
              <span className="text-xs font-normal text-gray-500">（縦長・固定）</span>
            </span>
            <span className="block text-[11px] text-gray-400 flex items-center gap-1">
              <Smartphone className="w-3 h-3" />
              TikTok・Reels・Shorts向け
            </span>
          </span>
        </div>
      </div>

      {/* 自由コメント */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">追加の指示（任意）</p>
        <TaskyTextarea
          value={value.freeText}
          disabled={disabled}
          onChange={(e) => onChange({ freeText: e.target.value.slice(0, 500) })}
          rows={2}
          maxLength={500}
          placeholder="例: 忙しい朝の時短をテーマに。若い母親が自宅のキッチンで話すイメージ。"
        />
      </div>

      {/* 詳細設定 */}
      <div>
        <button
          type="button"
          onClick={() => setAdvOpen((v) => !v)}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          <ChevronRight className={`w-[18px] h-[18px] transition-transform ${advOpen ? 'rotate-90' : ''}`} />
          <span>詳細設定</span>
        </button>
        {advOpen && (
          <div className="mt-3 bg-white border border-warm-border rounded-xl p-4 grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">必ず含める言葉</label>
              <TaskyInput
                value={value.requiredWordsText}
                disabled={disabled}
                onChange={(e) => onChange({ requiredWordsText: e.target.value })}
                placeholder={`例: ${productNamePlaceholder ?? '商品名'}`}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">避ける言葉</label>
              <TaskyInput
                value={value.bannedWordsText}
                disabled={disabled}
                onChange={(e) => onChange({ bannedWordsText: e.target.value })}
                placeholder="例: 安い、No.1"
              />
            </div>
            <p className="md:col-span-2 text-[11px] text-gray-400">
              話し方のトーンやシチュエーションの細かい指定は「追加の指示」に書いてください。
              CTA（締めカット＝行動を促すひと言）の文言は⑤プレビューで設定できます。
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UgcAdSettingsForm;
